  import api from './api';
  import type { EventFormData } from '../features/event-form-feature/EventFormContainer';

const isFile = (value: unknown): value is File => typeof File !== 'undefined' && value instanceof File;
const isBase64DataUrl = (value: unknown): value is string =>
  typeof value === 'string' && /^data:/i.test(value);

const toIsoDate = (value: unknown): string | undefined => {
  if (!value) return undefined;
  const d = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const sanitizeEventPayload = (eventData: EventFormData): EventFormData => ({
  title: eventData.title,
  slug: eventData.slug,
  description: eventData.description,
  startDate: eventData.startDate,
  endDate: eventData.endDate,
  location: eventData.location,
  mapLink: eventData.mapLink,
  address: eventData.address,
  city: eventData.city,
  state: eventData.state,
  category: eventData.category,
  capacity: eventData.capacity,
  showRemainingSpots: eventData.showRemainingSpots,
  isPaid: eventData.isPaid,
  tickets: eventData.tickets,
  ticketCatalogMode: eventData.ticketCatalogMode,
  ticketTypes: eventData.ticketTypes,
  salesPhases: eventData.salesPhases,
  ticketLinks: eventData.ticketLinks,
  hasStore: eventData.hasStore,
  products: eventData.products,
  registrationDeadline: eventData.registrationDeadline,
  customFields: eventData.customFields,
  coverImage: eventData.coverImage,
  galleryImages: eventData.galleryImages,
  contactEmail: eventData.contactEmail,
  contactPhone: eventData.contactPhone,
  showMap: eventData.showMap,
  showSocialShare: eventData.showSocialShare,
  customTheme: eventData.customTheme,
  pageTemplate: eventData.pageTemplate,
  postRegistrationUrl: eventData.postRegistrationUrl,
  status: eventData.status,
  allowWaitlist: eventData.allowWaitlist,
  allowGroupRegistration: eventData.allowGroupRegistration,
  requireApproval: eventData.requireApproval,
  sendConfirmationEmail: eventData.sendConfirmationEmail,
  allowInstallments: eventData.allowInstallments,
});

  // Interface para resposta da API
  interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
  }

  interface UpgradeBatchedV1Response extends ApiResponse<unknown> {
    warnings?: string[];
  }

  // Interface para evento retornado pela API
  export interface EventResponse {
    _id: string;
    title: string;
    slug:string;
    description: string;
    startDate: string;
    endDate: string;
    location: string;
    address: string;
    city: string;
    state: string;
    category: string;
    capacity: number;
    isPaid: boolean;
    /** Pessoas pagas (soma de quantity). */
    registeredCount?: number;
    /** Arrecadação bruta paga. */
    grossPaid?: number;
    /** Líquido da casa (bruto − taxa da plataforma). */
    netRevenue?: number;
    /** true quando o evento não tem ingresso pago. */
    isFreeEvent?: boolean;
    status: 'draft' | 'published' | 'cancelled' | 'completed';
    coverImage?: string;
    galleryImages?: string[];
    createdAt: string;
    updatedAt: string;
    parishId?: string;
    originParish?: {
      id: string;
      name: string;
      isSector: boolean;
    } | null;
    tickets: Array<{
      _id: string;
      name: string;
      description: string;
      price: number;
      quantity: number;
      startSaleDate?: string;
      endSaleDate?: string;
      eventId: string;
    }>;
    customFields: Array<{
      _id: string;
      fieldName: string;
      fieldType: 'text' | 'number' | 'select' | 'checkbox' | 'label' | 'cpf' | 'cnpj' | 'idade' | 'cep' | 'dataNascimento';
      isRequired: boolean;
      options?: string[];
      eventId: string;
      order: number;
    }>;
  }

const preserveImageUrl = (value: unknown): string | null =>
  typeof value === 'string' && !isBase64DataUrl(value) ? value : null;

async function uploadEventMediaFiles(id: string, sanitizedData: EventFormData): Promise<void> {
  const galleryFiles = (sanitizedData.galleryImages || []).filter(isFile);
  const products = sanitizedData.products || [];
  const hasProductImageFiles = products.some((product) => isFile(product.image));
  if (galleryFiles.length === 0 && !hasProductImageFiles) return;

  const formData = new FormData();
  const patch: Record<string, unknown> = {};

  if (hasProductImageFiles) {
    patch.products = products.map((product) => ({
      ...product,
      image: isFile(product.image) ? null : preserveImageUrl(product.image),
    }));
  }

  formData.append('eventData', JSON.stringify(patch));
  galleryFiles.forEach((file) => formData.append('galleryImages', file));
  products.forEach((product) => {
    if (isFile(product.image)) {
      formData.append('productImages', product.image);
    }
  });

  await api.put(`/events/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

  /** Resposta de GET /public/events/highlights (subset do evento + tickets populados). */
  export interface PublicEventTag {
    id: string;
    label: string;
    shortLabel?: string;
    description?: string;
    type: 'system' | 'manual';
    color: string;
    backgroundColor: string;
  }

  export interface PublicHighlightedEvent {
    _id: string;
    slug: string;
    title: string;
    description?: string;
    startDate: string;
    endDate?: string;
    location?: string;
    city?: string;
    state?: string;
    coverImage?: string;
    category?: string;
    isPaid?: boolean;
    tags?: PublicEventTag[];
    tickets?: Array<{ price: number; isActive?: boolean; hiddenFromPublicPage?: boolean }>;
  }

  // Serviço para eventos
  export const eventService = {
    // Listar todos os eventos
    getEvents: async (filters?: {
      parishId?: string;
      status?: string;
      category?: string;
      /** Abas da lista do painel: só a visão pedida + counts de todas. */
      listView?: 'ativos' | 'rascunhos' | 'encerrados' | 'todos';
    }) => {
      try {
        const response = await api.get<
          ApiResponse<EventResponse[]> & {
            counts?: {
              ativos: number;
              rascunhos: number;
              encerrados: number;
              todos: number;
            };
            listView?: string;
          }
        >('/events', { params: filters });
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar eventos:', error);
        throw error;
      }
    },
    
    getEventById: async (id: string) => {
      try {
        const response = await api.get(`/public/events/${id}`);
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar evento público:', error);
        throw error;
      }
    },

    /** Buscar evento por ID (dashboard) - inclui rascunhos e qualquer status */
    getEvent: async (id: string) => {
      try {
        const response = await api.get(`/events/${id}`);
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar evento:', error);
        throw error;
      }
    },

    /** Buscar evento leve para workspace/editor (sem inscrições pesadas). */
    getEventWorkspace: async (id: string) => {
      try {
        const response = await api.get(`/events/${id}/workspace`);
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar evento para workspace:', error);
        throw error;
      }
    },

    getEventBySlug: async (eventSlug: string, ticketId?: string) => {
      try {
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        const params: Record<string, string> = {};
        if (ticketId) params.ticketId = ticketId;
        if (host) params.host = host;
        const response = await api.get(`/public/${eventSlug}`, { params });
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar evento por slug:', error);
        throw error;
      }
    },

    getHighlightedEvents: async () => {
      try {
        const host = typeof window !== 'undefined' ? window.location.hostname : '';
        const response = await api.get<ApiResponse<PublicHighlightedEvent[]>>('/public/events/highlights', {
          params: host ? { host } : undefined,
        });
        return response.data;
      } catch (error) {
        console.error('Erro ao buscar eventos em destaque:', error);
        throw error;
      }
    },

    // Criar um novo evento
    createEvent: async (eventData: EventFormData) => {
      try {
        const sanitizedData = sanitizeEventPayload(eventData);
        const hasImageFiles =
          isFile(sanitizedData.coverImage) ||
          (sanitizedData.galleryImages || []).some((image) => isFile(image)) ||
          (sanitizedData.products || []).some((product) => isFile(product.image));

        console.log('🛒 [CREATE_EVENT] Dados completos do evento:', eventData);
        console.log('🛒 [CREATE_EVENT] Produtos:', eventData.products);
        console.log('🛒 [CREATE_EVENT] hasStore:', eventData.hasStore);
        if (hasImageFiles) {
          const formData = new FormData();
          
          const {
            coverImage,
            galleryImages,
            ...eventDataWithoutImages
          } = sanitizedData;
          
          formData.append('eventData', JSON.stringify(eventDataWithoutImages));
          
          if (isFile(coverImage)) {
            formData.append('coverImage', coverImage);
          }
          
          if (galleryImages && galleryImages.length > 0) {
            galleryImages.forEach((image, index) => {
              if (!isFile(image)) return;
              formData.append(`galleryImages`, image);
            });
          }
          
          // Adicionar imagens dos produtos
          if (sanitizedData.products && sanitizedData.products.length > 0) {
            sanitizedData.products.forEach((product, index) => {
              if (isFile(product.image)) {
                formData.append(`productImages`, product.image);
              }
            });
          }
          
          const response = await api.post<ApiResponse<EventResponse>>('/events', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          console.log('✅ [CREATE_EVENT] Evento criado:', response.data);
          return response.data;
        } else {
          const response = await api.post<ApiResponse<EventResponse>>('/events', sanitizedData);
          
          console.log('✅ [CREATE_EVENT] Evento criado:', response.data);
          return response.data;
        }
      } catch (error) {
        console.error('Erro ao criar evento:', error);
        throw error;
      }
    },

    duplicateEvent: async (eventId: string, payload: { slug: string; title?: string }) => {
      const response = await api.post<ApiResponse<EventResponse>>(`/events/${eventId}/duplicate`, payload);
      return response.data;
    },

    saveDraft: async (eventData: EventFormData, id?: string) => {
      try {
        const sanitizedData = sanitizeEventPayload(eventData);
        const hasImageFiles =
          isFile(sanitizedData.coverImage) ||
          (sanitizedData.galleryImages || []).some((image) => isFile(image)) ||
          (sanitizedData.products || []).some((product) => isFile(product.image));

        if (hasImageFiles) {
          const formData = new FormData();
          
          // Adicionar dados do evento como JSON
          const {
            coverImage,
            galleryImages,
            ...eventDataWithoutImages
          } = sanitizedData;
          
          formData.append('eventData', JSON.stringify(eventDataWithoutImages));
          
          // Adicionar imagem de capa
          if (isFile(coverImage)) {
            formData.append('coverImage', coverImage);
          }
          
          if (galleryImages && galleryImages.length > 0) {
            galleryImages.forEach((image, index) => {
              if (!isFile(image)) return;
              formData.append(`galleryImages`, image);
            });
          }
          
          // Adicionar imagens dos produtos
          if (sanitizedData.products && sanitizedData.products.length > 0) {
            sanitizedData.products.forEach((product, index) => {
              if (isFile(product.image)) {
                formData.append(`productImages`, product.image);
              }
            });
          }
          
          const endpoint = id ? `/events/draft/${id}` : '/events/draft';
          const response = await api.post<ApiResponse<EventResponse>>(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          
          return response.data;
        } else {
          const endpoint = id ? `/events/draft/${id}` : '/events/draft';
          const response = await api.post<ApiResponse<EventResponse>>(endpoint, sanitizedData);
          return response.data;
        }
      } catch (error) {
        console.error('Erro ao salvar rascunho:', error);
        throw error;
      }
    },

    updateEvent: async (id: string, eventData: EventFormData) => {
      try {
        const sanitizedData = sanitizeEventPayload(eventData);
        const coverImageFile = isFile(sanitizedData.coverImage) ? sanitizedData.coverImage : null;
        const hasGalleryImageFiles = (sanitizedData.galleryImages || []).some((image) => isFile(image));
        const hasProductImageFiles = (sanitizedData.products || []).some((product) => isFile(product.image));
        const hasBase64ImageStrings =
          isBase64DataUrl(sanitizedData.coverImage) ||
          (sanitizedData.galleryImages || []).some((image) => isBase64DataUrl(image)) ||
          (sanitizedData.products || []).some((product) => isBase64DataUrl(product.image));

        // Evita 413 no endpoint principal: update sempre via JSON leve.
        const payloadWithoutBinary = {
          ...sanitizedData,
          coverImage:
            typeof sanitizedData.coverImage === 'string' && !isBase64DataUrl(sanitizedData.coverImage)
              ? sanitizedData.coverImage
              : null,
          galleryImages: (sanitizedData.galleryImages || []).filter(
            (image): image is string => typeof image === 'string' && !isBase64DataUrl(image),
          ),
          products: (sanitizedData.products || []).map((product) => ({
            ...product,
            image: isFile(product.image) ? null : preserveImageUrl(product.image),
          })),
        };

        const response = await api.put<ApiResponse<EventResponse>>(`/events/${id}`, payloadWithoutBinary);

        // Mantém suporte a troca de capa sem inflar o PUT principal.
        if (coverImageFile) {
          await eventService.updateEventCover(id, coverImageFile);
        }

        if (hasGalleryImageFiles || hasProductImageFiles) {
          await uploadEventMediaFiles(id, sanitizedData);
        }
        if (hasBase64ImageStrings) {
          console.warn(
            `[updateEvent] Strings base64 de imagem detectadas para ${id} e removidas do PUT para evitar 413.`,
          );
        }

        return response.data;
      } catch (error) {
        console.error(`Erro ao atualizar evento ${id}:`, error);
        throw error;
      }
    },

    /** PATCH parcial via PUT (ex.: só metaPixel) — não passa pelo sanitize do formulário completo. */
    updateEventFields: async (id: string, fields: Record<string, unknown>) => {
      const response = await api.put<ApiResponse<EventResponse>>(`/events/${id}`, fields);
      return response.data;
    },

    // Publicar um evento
    publishEvent: async (id: string) => {
      try {
        const response = await api.put<ApiResponse<EventResponse>>(`/events/${id}/publish`);
        return response.data;
      } catch (error) {
        console.error(`Erro ao publicar evento ${id}:`, error);
        throw error;
      }
    },

    // Atualizar apenas a imagem de capa
    updateEventCover: async (id: string, file: File) => {
      try {
        const formData = new FormData();
        formData.append('coverImage', file);
        const response = await api.patch<ApiResponse<EventResponse>>(`/events/${id}/cover`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
      } catch (error) {
        console.error(`Erro ao atualizar capa do evento ${id}:`, error);
        throw error;
      }
    },

    // Excluir um evento
    deleteEvent: async (id: string) => {
      try {
        const response = await api.delete<ApiResponse<{}>>(`/events/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Erro ao excluir evento ${id}:`, error);
        throw error;
      }
    },

    migrateTicketCatalog: async (id: string) => {
      const response = await api.post<ApiResponse<unknown>>(`/events/${id}/ticket-catalog/migrate-to-batched`);
      return response.data;
    },

    migrateToPhased: async (id: string, payload?: unknown) => {
      const response = await api.post<ApiResponse<unknown>>(
        `/events/${id}/ticket-catalog/migrate-to-phased`,
        payload || {}
      );
      return response.data;
    },

    upgradeBatchedV1: async (id: string): Promise<UpgradeBatchedV1Response> => {
      const response = await api.post<UpgradeBatchedV1Response>(
        `/events/${id}/ticket-catalog/upgrade-batched-v1`
      );
      return response.data;
    },

    previewUpgradeBatchedV1: async (id: string) => {
      const response = await api.get<ApiResponse<{ conflicts: string[] }>>(
        `/events/${id}/ticket-catalog/upgrade-batched-v1-preview`
      );
      return response.data;
    },

    activateSalesPhase: async (eventId: string, phaseId: string) => {
      const response = await api.post<ApiResponse<unknown>>(
        `/events/${eventId}/sales-phases/${phaseId}/activate`
      );
      return response.data;
    },

    getActiveSalesPhase: async (eventId: string) => {
      const response = await api.get<ApiResponse<{ _id: string; name: string; endSaleDate?: string }>>(
        `/events/${eventId}/sales-phases/active`
      );
      return response.data;
    },

    saveSalesPhases: async (
      eventId: string,
      payload: { ticketTypes: EventFormData['ticketTypes']; salesPhases: EventFormData['salesPhases'] }
    ) => {
      const response = await api.put<
        ApiResponse<{ ticketTypes: EventFormData['ticketTypes']; salesPhases: EventFormData['salesPhases'] }>
      >(`/events/${eventId}/sales-phases`, {
        ticketTypes: payload.ticketTypes?.map((type) => ({
          ...type,
          batches: type?.batches?.map((batch) => ({
            ...batch,
            startSaleDate: toIsoDate(batch.startSaleDate),
            endSaleDate: toIsoDate(batch.endSaleDate),
          })),
        })),
        salesPhases: payload.salesPhases?.map((phase) => ({
          ...phase,
          startSaleDate: toIsoDate(phase?.startSaleDate),
          endSaleDate: toIsoDate(phase?.endSaleDate),
        })),
      });
      return response.data;
    },

    revertTicketCatalog: async (id: string) => {
      const response = await api.post<ApiResponse<unknown>>(`/events/${id}/ticket-catalog/revert-to-legacy`);
      return response.data;
    },

    getTicketCatalogRevertStatus: async (id: string) => {
      const response = await api.get<ApiResponse<{ canRevert: boolean; reason?: string }>>(
        `/events/${id}/ticket-catalog/revert-status`
      );
      return response.data;
    },

    getMigrateToPhasedStatus: async (id: string) => {
      const response = await api.get<ApiResponse<{ canMigrate: boolean; soldCount: number; reason?: string }>>(
        `/events/${id}/ticket-catalog/migrate-to-phased-status`
      );
      return response.data;
    },

    getTicketLinks: async (id: string) => {
      const response = await api.get<ApiResponse<unknown[]>>(`/events/${id}/ticket-links`);
      return response.data;
    },

    saveTicketLinks: async (id: string, links: unknown[]) => {
      const response = await api.put<ApiResponse<unknown[]>>(`/events/${id}/ticket-links`, { links });
      return response.data;
    },
  };
